/**
 * カツオポータル ホーム（Microsoft Viva Connections 風）
 * グラデーションのヒーロー → ダッシュボードカード → 社内フィード（Engage風）＋右レール。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, MessagesSquare, BookOpen, Receipt, FileCheck,
  Clock, ChevronRight, Paperclip, Pin, BadgeCheck, Search, Sparkles,
} from 'lucide-react';
import * as api from '../../services/api';
import PortalLayout, { PV, Avatar } from './PortalLayout';
import { fmtDateTime, EmptyState } from './ui';

interface FeedPost {
  id: string; post_type: string; title: string; author_name: string | null; created_at: string;
  deadline: string | null; pinned?: number; excerpt?: string | null; attachment_count?: number; my_read: boolean;
}
interface Summary {
  board_unread: number; circular_pending: number; chat_unread: number; manuals_unread: number;
  my_expenses_active: number; my_requests_pending: number;
  approvals_pending: number; approvals_expenses: number; approvals_requests: number;
  office_today: { clock_in: string | null; clock_out: string | null } | null;
  recent_posts: FeedPost[];
  is_admin: boolean;
}
interface RoomRow { id: string; room_type: string; name: string | null; unread: number; last_message: { body: string | null; sender_name: string | null } | null }

const card: React.CSSProperties = { background: '#fff', borderRadius: 12, boxShadow: PV.cardShadow, padding: '16px 18px' };

export default function PortalHomePage() {
  const nav = useNavigate();
  const [sum, setSum] = useState<Summary | null>(null);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [punching, setPunching] = useState(false);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const user = (() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } })();

  const load = useCallback(() => {
    api.get<{ data: Summary }>('/portal/summary').then(r => setSum(r.data)).catch(() => { /* noop */ });
    api.get<{ data: RoomRow[] }>('/portal/chat/rooms').then(r => setRooms((r.data ?? []).slice(0, 4))).catch(() => { /* noop */ });
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, [load]);

  const punch = async (type: 'in' | 'out') => {
    setPunching(true); setMsg('');
    try {
      const r = await api.post<{ data: { clock_in: string | null; clock_out: string | null } }>('/portal/office/punch', { type });
      setMsg(type === 'in' ? `出勤 ${r.data?.clock_in}` : `退勤 ${r.data?.clock_out}`);
      load();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : '打刻に失敗しました'); }
    finally { setPunching(false); }
  };

  const today = sum?.office_today;
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? 'おはようございます' : hour < 18 ? 'こんにちは' : 'おつかれさまです';
  const dateLabel = now.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'long' });

  const sectionTitle = (icon: React.ReactNode, label: string, onMore?: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 2px 10px' }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: PV.text, display: 'flex', alignItems: 'center', gap: 7 }}>{icon}{label}</div>
      {onMore && (
        <button onClick={onMore} style={{ border: 'none', background: 'none', color: PV.accent, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          すべて表示 <ChevronRight size={11} style={{ verticalAlign: -2 }} />
        </button>
      )}
    </div>
  );

  return (
    <PortalLayout wide>
      {/* 見出し（白基調・高コントラスト） */}
      <div style={{
        background: '#fff', border: '1px solid #e3e6ea', borderRadius: 12, padding: '20px 24px',
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 16,
        boxShadow: PV.cardShadow,
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: PV.textSub }}>{dateLabel}</div>
          <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.3px', marginTop: 3, color: PV.text }}>
            {greeting}、{user?.user_name ?? ''} さん
          </div>
          <div style={{ fontSize: 13.5, color: PV.textSub, marginTop: 4 }}>
            {sum ? (
              sum.board_unread + sum.chat_unread + sum.manuals_unread > 0
                ? <>未読: <strong style={{ color: PV.accentDark }}>連絡 {sum.board_unread}件 ・ チャット {sum.chat_unread}件 ・ 要確認 {sum.manuals_unread}件</strong></>
                : '新しい連絡はありません。'
            ) : ' '}
          </div>
        </div>
        {/* マニュアル横断検索 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, background: '#fff', border: '1.5px solid #c8cdd3', borderRadius: 10, padding: '2px 2px 2px 14px', minWidth: 300 }}>
          <Search size={15} color="#50545b" />
          <input
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') nav(`/portal/manuals?q=${encodeURIComponent(q)}`); }}
            placeholder="マニュアル・規程を全文検索"
            style={{ border: 'none', outline: 'none', background: 'none', padding: '10px 10px', fontSize: 14, flex: 1, color: PV.text, fontFamily: 'inherit' }} />
          <button onClick={() => nav(`/portal/manuals?q=${encodeURIComponent(q)}`)}
            style={{ border: 'none', background: PV.accent, color: '#fff', borderRadius: 8, padding: '10px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            検索
          </button>
        </div>
      </div>

      {/* ダッシュボードカード（Connections風） */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 20 }}>
        {/* 打刻 */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: PV.textSub, marginBottom: 10 }}>
            <Clock size={14} color={PV.accent} /> 陸上勤怠
            <button onClick={() => nav('/portal/office')} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: PV.accent, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>勤怠表 ›</button>
          </div>
          <div style={{ fontSize: 12.5, color: PV.textSub, marginBottom: 10, minHeight: 18 }}>
            {msg || (today?.clock_in ? `出勤 ${today.clock_in}${today.clock_out ? ` ／ 退勤 ${today.clock_out}` : ' ・ 勤務中'}` : '本日の打刻はまだです')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={punching || !!today?.clock_in} onClick={() => punch('in')}
              style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', cursor: today?.clock_in ? 'not-allowed' : 'pointer', background: today?.clock_in ? '#e1dfdd' : PV.accent, color: today?.clock_in ? '#a19f9d' : '#fff', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit' }}>
              出勤
            </button>
            <button disabled={punching || !today?.clock_in || !!today?.clock_out} onClick={() => punch('out')}
              style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1.5px solid ${PV.accent}`, cursor: (!today?.clock_in || today?.clock_out) ? 'not-allowed' : 'pointer', background: '#fff', color: (!today?.clock_in || today?.clock_out) ? '#a19f9d' : PV.accent, fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', borderColor: (!today?.clock_in || today?.clock_out) ? '#e1dfdd' : PV.accent }}>
              退勤
            </button>
          </div>
        </div>

        {/* 承認待ち or 自分の申請 */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: PV.textSub, marginBottom: 10 }}>
            <BadgeCheck size={14} color={PV.accent} /> {sum?.is_admin ? 'あなたの承認待ち' : 'わたしの申請'}
          </div>
          {sum?.is_admin ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <QuickRow label="経費の承認・精算" count={sum.approvals_expenses} onClick={() => nav('/portal/expenses')} />
              <QuickRow label="各種申請の承認" count={sum.approvals_requests} onClick={() => nav('/portal/requests')} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <QuickRow label="経費（進行中）" count={sum?.my_expenses_active ?? 0} onClick={() => nav('/portal/expenses')} />
              <QuickRow label="各種申請（承認待ち）" count={sum?.my_requests_pending ?? 0} onClick={() => nav('/portal/requests')} />
            </div>
          )}
        </div>

        {/* クイックアクション */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: PV.textSub, marginBottom: 10 }}>
            <Sparkles size={14} color={PV.accent} /> クイックアクション
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
            {[
              { label: '経費を申請', icon: Receipt, path: '/portal/expenses' },
              { label: '休暇を申請', icon: FileCheck, path: '/portal/requests' },
              { label: 'チャット', icon: MessagesSquare, path: '/portal/chat' },
              { label: 'マニュアル', icon: BookOpen, path: '/portal/manuals' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <button key={a.label} onClick={() => nav(a.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 10px', borderRadius: 8, border: 'none', background: PV.accentSoft, color: PV.accentDark, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Icon size={14} /> {a.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* フィード＋右レール */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 330px', gap: 18, alignItems: 'start' }}>
        {/* 社内フィード（Engage風） */}
        <div>
          {sectionTitle(<Megaphone size={15} color={PV.accent} />, '社内フィード', () => nav('/portal/board'))}
          {!sum || sum.recent_posts.length === 0 ? (
            <div style={{ ...card, padding: 10 }}><EmptyState img="/portal/empty-board.png" text="投稿はまだありません" /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {sum.recent_posts.map(p => (
                <button key={p.id} onClick={() => nav(`/portal/board?post=${p.id}`)}
                  style={{ ...card, textAlign: 'left', cursor: 'pointer', border: 'none', padding: '14px 18px', fontFamily: 'inherit' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={p.author_name} size={36} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: PV.text }}>
                        {p.author_name}
                        <span style={{ fontWeight: 400, color: '#5c6066', fontSize: 11.5, marginLeft: 8 }}>{fmtDateTime(p.created_at)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 1 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: p.post_type === 'circular' ? '#fff4ce' : PV.accentSoft, color: p.post_type === 'circular' ? '#8a6914' : PV.accentDark }}>
                          {p.post_type === 'circular' ? '回覧' : 'お知らせ'}
                        </span>
                        {(p.pinned ?? 0) === 1 && <Pin size={11} color="#d13438" />}
                        {p.deadline && <span style={{ fontSize: 11, color: '#8a6914' }}>期限 {p.deadline.replace(/-/g, '/')}</span>}
                      </div>
                    </div>
                    {!p.my_read && <span style={{ background: '#d13438', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '2px 8px', flexShrink: 0 }}>未読</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: PV.text, margin: '10px 0 4px' }}>{p.title}</div>
                  {p.excerpt && <div style={{ fontSize: 13, color: PV.textSub, lineHeight: 1.7, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.excerpt}</div>}
                  {(p.attachment_count ?? 0) > 0 && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: 11.5, color: PV.accentDark, background: PV.accentSoft, borderRadius: 6, padding: '3px 9px', fontWeight: 600 }}>
                      <Paperclip size={11} /> 資料 {p.attachment_count}件
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 右レール */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            {sectionTitle(<MessagesSquare size={15} color={PV.accent} />, 'チャット', () => nav('/portal/chat'))}
            <div style={{ ...card, padding: '8px 14px' }}>
              {rooms.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#5c6066', padding: '8px 0' }}>チャットはまだありません</div>
              ) : rooms.map(r => (
                <button key={r.id} onClick={() => nav('/portal/chat')}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '8px 2px', border: 'none', borderBottom: '1px solid #f3f2f1', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Avatar name={r.name} size={28} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: r.unread > 0 ? 800 : 600, color: PV.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.room_type === 'group' ? `${r.name}` : r.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#5c6066', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.last_message ? `${r.last_message.sender_name ?? ''}: ${r.last_message.body ?? ''}` : ''}
                    </div>
                  </div>
                  {r.unread > 0 && <span style={{ background: '#d13438', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '1px 7px', flexShrink: 0 }}>{r.unread}</span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            {sectionTitle(<BookOpen size={15} color={PV.accent} />, 'リソース')}
            <div style={{ ...card, padding: '8px 14px' }}>
              {[
                { label: '社内マニュアル・規程集', path: '/portal/manuals' },
                { label: '経費精算の手引き', path: '/portal/manuals?q=経費' },
                { label: '各種申請の様式一覧', path: '/portal/requests' },
                { label: '勤怠（船員）・月次集計', path: '/attendance' },
              ].map(l => (
                <button key={l.label} onClick={() => nav(l.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '9px 2px', border: 'none', borderBottom: '1px solid #f3f2f1', background: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: PV.text, fontFamily: 'inherit' }}>
                  <ChevronRight size={13} color={PV.accent} /> {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}

function QuickRow({ label, count, onClick }: { label: string; count: number; onClick: () => void }) {
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: count > 0 ? '#fff4ce' : '#faf9f8', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
      <span style={{ fontSize: 12.5, color: PV.text, fontWeight: 600, flex: 1 }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: count > 0 ? '#8a6914' : '#a19f9d' }}>{count}件</span>
    </button>
  );
}
